import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../../store/hooks';
import { selectCurrentApplication } from '../../../store/slices/application.slice';
import { Link } from 'react-router-dom';
import ApplicationRequired from '../../../components/common/ApplicationRequired';
import {
  ClipboardDocumentIcon,
  CheckIcon,
  CodeBracketIcon,
  CommandLineIcon,
  CubeIcon,
  ArrowTopRightOnSquareIcon,
  BookOpenIcon,
  DevicePhoneMobileIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import MainLayout from '../../../components/layout/MainLayout';
import sdkService from '../../../services/api/sdk.service';

type Platform = 'ios' | 'android' | 'react-native' | 'flutter';

export default function MobileSDKSetupPage() {
  const { t } = useTranslation('mobile');
  const navigate = useNavigate();
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const isAllAppsSelected = !selectedAppId || selectedAppId === 'all';
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [platform, setPlatform] = useState<Platform>('react-native');
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [appId, setAppId] = useState('');
  const [apiEndpoint, setApiEndpoint] = useState('');
  const [previousAppId, setPreviousAppId] = useState<string | null>(null);

  // SDK Configuration States
  const [config, setConfig] = useState({
    sessionTracking: {
      enabled: true,
      trackScreenViews: true,
      trackUserInteractions: true,
    },
    crashReporting: {
      enabled: true,
      captureNativeCrashes: true,
      captureBreadcrumbs: true,
    },
    performance: {
      enabled: true,
      trackAppStartTime: true,
      trackScreenRenderTime: true,
      trackNetworkRequests: true,
    },
    analytics: {
      enabled: true,
      trackUserProperties: true,
    },
  });

  // Load SDK configuration from backend
  useEffect(() => {
    const loadSDKConfig = async () => {
      if (!selectedAppId) {
        setLoading(false);
        setApiKey('');
        setAppId('');
        return;
      }

      try {
        setLoading(true);
        const sdkConfig = await sdkService.getSDKConfiguration(selectedAppId);
        setApiKey(sdkConfig.apiKey);
        setAppId(sdkConfig.appId);
        setApiEndpoint(sdkConfig.apiEndpoint);
      } catch (error) {
        console.error('Failed to load SDK configuration:', error);
        setApiKey('');
        setAppId('');
      } finally {
        setLoading(false);
      }
    };

    loadSDKConfig();
  }, [selectedAppId]);

  // Monitor global application changes
  useEffect(() => {
    if (isAllAppsSelected && previousAppId) {
      toast.error(
        t('errors.cannotUseAllApps') || 'Cannot configure Mobile SDK with "All Applications" selected. Please select a specific application.',
        {
          duration: 6000,
          icon: '⚠️',
        }
      );
    } else if (selectedAppId && selectedAppId !== 'all' && previousAppId && selectedAppId !== previousAppId) {
      toast.success(
        t('info.applicationSwitched') || `Application switched successfully`,
        {
          duration: 3000,
          icon: '🔄',
        }
      );
    }

    if (selectedAppId !== previousAppId) {
      setPreviousAppId(selectedAppId);
    }
  }, [selectedAppId, isAllAppsSelected, previousAppId, t]);

  const copyToClipboard = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  // Platform-specific installation code
  const getInstallCode = () => {
    switch (platform) {
      case 'ios':
        return `# Swift Package Manager (Xcode: File → Add Package Dependencies)
https://github.com/devskin1/devskin-monitoring-mobile-sdk-ios.git

# Or in Package.swift
dependencies: [
    .package(url: "https://github.com/devskin1/devskin-monitoring-mobile-sdk-ios.git", from: "1.0.0")
]`;

      case 'android':
        return `// settings.gradle (add JitPack repository)
dependencyResolutionManagement {
    repositories {
        google()
        mavenCentral()
        maven { url 'https://jitpack.io' }
    }
}

// build.gradle (app level)
dependencies {
    implementation 'com.github.devskin1:devskin-monitoring-mobile-sdk-android:v1.0.0'
}`;

      case 'react-native':
        return `# npm
npm install @devskin/mobile-sdk-react-native

# yarn
yarn add @devskin/mobile-sdk-react-native

# iOS pod install (after npm install)
cd ios && pod install`;

      case 'flutter':
        return `# pubspec.yaml
dependencies:
  devskin_mobile_sdk: ^1.0.0

# Then run
flutter pub get`;
    }
  };

  const getInitCode = () => {
    switch (platform) {
      case 'ios':
        return `import DevSkinMobileSDK

// AppDelegate.swift - didFinishLaunchingWithOptions
func application(_ application: UIApplication, didFinishLaunchingWithOptions launchOptions: [UIApplication.LaunchOptionsKey: Any]?) -> Bool {

    DevSkin.shared.initialize(
        apiKey: "${apiKey}",
        appId: "${appId}",
        config: DevSkinConfig(
            apiUrl: "https://api-monitoring.devskin.com",
            environment: .production,

            // Session Tracking
            sessionTracking: SessionConfig(
                enabled: ${config.sessionTracking.enabled},
                trackScreenViews: ${config.sessionTracking.trackScreenViews},
                trackUserInteractions: ${config.sessionTracking.trackUserInteractions}
            ),

            // Crash Reporting
            crashReporting: CrashConfig(
                enabled: ${config.crashReporting.enabled},
                captureNativeCrashes: ${config.crashReporting.captureNativeCrashes},
                captureBreadcrumbs: ${config.crashReporting.captureBreadcrumbs}
            ),

            // Performance Monitoring
            performance: PerformanceConfig(
                enabled: ${config.performance.enabled},
                trackAppStartTime: ${config.performance.trackAppStartTime},
                trackScreenRenderTime: ${config.performance.trackScreenRenderTime},
                trackNetworkRequests: ${config.performance.trackNetworkRequests}
            )
        )
    )

    return true
}`;

      case 'android':
        return `import com.devskin.sdk.DevSkin
import com.devskin.sdk.DevSkinConfig

// Application class or MainActivity
class MyApplication : Application() {
    override fun onCreate() {
        super.onCreate()

        DevSkin.initialize(
            context = this,
            apiKey = "${apiKey}",
            appId = "${appId}",
            config = DevSkinConfig(
                apiUrl = "https://api-monitoring.devskin.com",
                environment = "production",

                // Session Tracking
                sessionTracking = SessionConfig(
                    enabled = ${config.sessionTracking.enabled},
                    trackScreenViews = ${config.sessionTracking.trackScreenViews},
                    trackUserInteractions = ${config.sessionTracking.trackUserInteractions}
                ),

                // Crash Reporting
                crashReporting = CrashConfig(
                    enabled = ${config.crashReporting.enabled},
                    captureNativeCrashes = ${config.crashReporting.captureNativeCrashes},
                    captureBreadcrumbs = ${config.crashReporting.captureBreadcrumbs}
                ),

                // Performance Monitoring
                performance = PerformanceConfig(
                    enabled = ${config.performance.enabled},
                    trackAppStartTime = ${config.performance.trackAppStartTime},
                    trackScreenRenderTime = ${config.performance.trackScreenRenderTime},
                    trackNetworkRequests = ${config.performance.trackNetworkRequests}
                )
            )
        )
    }
}`;

      case 'react-native':
        return `import DevSkin from '@devskin/mobile-sdk-react-native';

// App.tsx or index.js (before AppRegistry)
DevSkin.init({
  apiKey: '${apiKey}',
  appId: '${appId}',
  apiUrl: 'https://api-monitoring.devskin.com',
  environment: 'production',

  // Session Tracking
  sessionTracking: {
    enabled: ${config.sessionTracking.enabled},
    trackScreenViews: ${config.sessionTracking.trackScreenViews},
    trackUserInteractions: ${config.sessionTracking.trackUserInteractions},
  },

  // Crash Reporting
  crashReporting: {
    enabled: ${config.crashReporting.enabled},
    captureNativeCrashes: ${config.crashReporting.captureNativeCrashes},
    captureBreadcrumbs: ${config.crashReporting.captureBreadcrumbs},
  },

  // Performance Monitoring
  performance: {
    enabled: ${config.performance.enabled},
    trackAppStartTime: ${config.performance.trackAppStartTime},
    trackScreenRenderTime: ${config.performance.trackScreenRenderTime},
    trackNetworkRequests: ${config.performance.trackNetworkRequests},
  },
});`;

      case 'flutter':
        return `import 'package:devskin_mobile_sdk/devskin_mobile_sdk.dart';

// main.dart
void main() async {
  WidgetsFlutterBinding.ensureInitialized();

  await DevSkin.initialize(
    apiKey: '${apiKey}',
    appId: '${appId}',
    config: DevSkinConfig(
      apiUrl: 'https://api-monitoring.devskin.com',
      environment: 'production',

      // Session Tracking
      sessionTracking: SessionConfig(
        enabled: ${config.sessionTracking.enabled},
        trackScreenViews: ${config.sessionTracking.trackScreenViews},
        trackUserInteractions: ${config.sessionTracking.trackUserInteractions},
      ),

      // Crash Reporting
      crashReporting: CrashConfig(
        enabled: ${config.crashReporting.enabled},
        captureNativeCrashes: ${config.crashReporting.captureNativeCrashes},
        captureBreadcrumbs: ${config.crashReporting.captureBreadcrumbs},
      ),

      // Performance Monitoring
      performance: PerformanceConfig(
        enabled: ${config.performance.enabled},
        trackAppStartTime: ${config.performance.trackAppStartTime},
        trackScreenRenderTime: ${config.performance.trackScreenRenderTime},
        trackNetworkRequests: ${config.performance.trackNetworkRequests},
      ),
    ),
  );

  runApp(MyApp());
}`;
    }
  };

  const getTrackingExamples = () => {
    switch (platform) {
      case 'ios':
        return `// Track custom events
DevSkin.shared.track("button_tapped", properties: [
    "button_id": "purchase",
    "screen": "product_details",
    "price": 29.99
])

// Identify users
DevSkin.shared.identify(userId: "user-123", traits: [
    "email": "user@example.com",
    "name": "John Doe",
    "plan": "premium"
])

// Track screen views (automatic or manual)
DevSkin.shared.trackScreen("ProductDetails", properties: [
    "product_id": "abc123"
])

// Add breadcrumbs
DevSkin.shared.addBreadcrumb(
    category: "navigation",
    message: "User navigated to checkout",
    level: .info,
    data: ["from": "cart", "to": "checkout"]
)

// Capture errors manually
DevSkin.shared.captureError(error, context: [
    "feature": "checkout",
    "step": "payment"
])`;

      case 'android':
        return `// Track custom events
DevSkin.track("button_tapped", mapOf(
    "button_id" to "purchase",
    "screen" to "product_details",
    "price" to 29.99
))

// Identify users
DevSkin.identify("user-123", mapOf(
    "email" to "user@example.com",
    "name" to "John Doe",
    "plan" to "premium"
))

// Track screen views (automatic or manual)
DevSkin.trackScreen("ProductDetails", mapOf(
    "product_id" to "abc123"
))

// Add breadcrumbs
DevSkin.addBreadcrumb(
    category = "navigation",
    message = "User navigated to checkout",
    level = BreadcrumbLevel.INFO,
    data = mapOf("from" to "cart", "to" to "checkout")
)

// Capture errors manually
DevSkin.captureError(exception, mapOf(
    "feature" to "checkout",
    "step" to "payment"
))`;

      case 'react-native':
        return `import DevSkin from '@devskin/mobile-sdk-react-native';

// Track custom events
DevSkin.track('button_tapped', {
  button_id: 'purchase',
  screen: 'product_details',
  price: 29.99,
});

// Identify users
DevSkin.identify('user-123', {
  email: 'user@example.com',
  name: 'John Doe',
  plan: 'premium',
});

// Track screen views (use with React Navigation)
import { useNavigationState } from '@react-navigation/native';

const routeName = useNavigationState(state => state?.routes[state.index]?.name);
useEffect(() => {
  if (routeName) {
    DevSkin.trackScreen(routeName);
  }
}, [routeName]);

// Add breadcrumbs
DevSkin.addBreadcrumb({
  category: 'navigation',
  message: 'User navigated to checkout',
  level: 'info',
  data: { from: 'cart', to: 'checkout' },
});

// Capture errors manually
DevSkin.captureError(error, {
  feature: 'checkout',
  step: 'payment',
});`;

      case 'flutter':
        return `import 'package:devskin_mobile_sdk/devskin_mobile_sdk.dart';

// Track custom events
DevSkin.track('button_tapped', {
  'button_id': 'purchase',
  'screen': 'product_details',
  'price': 29.99,
});

// Identify users
DevSkin.identify('user-123', {
  'email': 'user@example.com',
  'name': 'John Doe',
  'plan': 'premium',
});

// Track screen views (use with Navigator observer)
class DevSkinNavigatorObserver extends NavigatorObserver {
  @override
  void didPush(Route route, Route? previousRoute) {
    if (route.settings.name != null) {
      DevSkin.trackScreen(route.settings.name!);
    }
  }
}

// In MaterialApp
MaterialApp(
  navigatorObservers: [DevSkinNavigatorObserver()],
  // ...
)

// Add breadcrumbs
DevSkin.addBreadcrumb(
  category: 'navigation',
  message: 'User navigated to checkout',
  level: BreadcrumbLevel.info,
  data: {'from': 'cart', 'to': 'checkout'},
);

// Capture errors manually
DevSkin.captureError(error, {
  'feature': 'checkout',
  'step': 'payment',
});`;
    }
  };

  const CodeBlock = ({ code, index, language = 'javascript' }: { code: string; index: number; language?: string }) => (
    <div className="relative group">
      <div className="flex items-center justify-between bg-gray-800 dark:bg-gray-900 px-4 py-2 rounded-t-lg">
        <span className="text-xs font-mono text-gray-400">{language}</span>
        <button
          onClick={() => copyToClipboard(code, index)}
          className="flex items-center space-x-2 text-sm text-gray-400 hover:text-white transition-colors"
        >
          {copiedIndex === index ? (
            <>
              <CheckIcon className="w-4 h-4 text-green-500" />
              <span className="text-green-500">{t('sdk.copied') || 'Copied!'}</span>
            </>
          ) : (
            <>
              <ClipboardDocumentIcon className="w-4 h-4" />
              <span>{t('sdk.copy') || 'Copy'}</span>
            </>
          )}
        </button>
      </div>
      <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 p-4 rounded-b-lg overflow-x-auto text-sm font-mono shadow-inner">
        <code>{code}</code>
      </pre>
    </div>
  );

  const platforms: { id: Platform; name: string; icon: string; description: string }[] = [
    { id: 'react-native', name: 'React Native', icon: '⚛️', description: 'JavaScript/TypeScript' },
    { id: 'flutter', name: 'Flutter', icon: '🦋', description: 'Dart' },
    { id: 'ios', name: 'iOS Native', icon: '🍎', description: 'Swift/Objective-C' },
    { id: 'android', name: 'Android Native', icon: '🤖', description: 'Kotlin/Java' },
  ];

  // Show elegant empty state when All Applications is selected
  if (isAllAppsSelected) {
    return (
      <MainLayout>
        <ApplicationRequired
          title={t('validation.title') || 'Application Selection Required'}
          message={t('validation.description') || 'Before configuring the Mobile SDK, you must select a specific application. The "All Applications" option cannot be used for SDK configuration.'}
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header with gradient */}
        <div className="relative overflow-hidden bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-rose-500/10 dark:from-purple-500/5 dark:via-pink-500/5 dark:to-rose-500/5 rounded-2xl p-8 border border-purple-200/50 dark:border-purple-800/50">
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-2">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-lg">
                <DevicePhoneMobileIcon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {t('sdk.title') || 'Mobile SDK Setup'}
              </h1>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
              {t('sdk.subtitle') || 'Instrument your iOS, Android, React Native, or Flutter apps for comprehensive mobile monitoring'}
            </p>
          </div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-400/20 to-pink-600/20 rounded-full blur-3xl -z-0" />
        </div>

        {!selectedAppId ? (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 p-12 text-center">
            <DevicePhoneMobileIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {t('validation.pleaseSelect') || 'Please select an application to continue'}
            </p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <>
            {/* Feature Highlight Banner */}
            <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <span className="text-3xl">📱</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold">{t('sdk.banner.title') || 'Complete Mobile Monitoring'}</h3>
                    <span className="text-xs bg-white/30 px-2 py-1 rounded-full backdrop-blur-sm">{t('sdk.banner.badge') || 'All Platforms'}</span>
                  </div>
                  <p className="text-purple-100 text-sm mb-3">
                    {t('sdk.banner.description') || 'Real-time crash reporting, performance monitoring, user analytics, and session tracking for your mobile apps.'}
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">Crash Reporting</span>
                    <span className="bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">Performance</span>
                    <span className="bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">User Analytics</span>
                    <span className="bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">Session Tracking</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-500 rounded-lg shadow-md">
                      <CodeBracketIcon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{t('sdk.apiKey') || 'API Key'}</h3>
                  </div>
                  <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-full">
                    Public Key
                  </span>
                </div>
                <code className="text-sm font-mono text-purple-700 dark:text-purple-300 break-all block bg-white/50 dark:bg-gray-900/50 px-3 py-2 rounded-lg mb-2">
                  {apiKey}
                </code>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 border border-pink-200 dark:border-pink-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-pink-500 rounded-lg shadow-md">
                    <CubeIcon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{t('sdk.appId') || 'App ID'}</h3>
                </div>
                <code className="text-sm font-mono text-pink-700 dark:text-pink-300 break-all block bg-white/50 dark:bg-gray-900/50 px-3 py-2 rounded-lg">
                  {appId}
                </code>
              </div>

              <div className="bg-gradient-to-br from-rose-50 to-red-50 dark:from-rose-900/20 dark:to-red-900/20 border border-rose-200 dark:border-rose-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-rose-500 rounded-lg shadow-md">
                    <CommandLineIcon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">API URL</h3>
                </div>
                <code className="text-sm font-mono text-rose-700 dark:text-rose-300 break-all block bg-white/50 dark:bg-gray-900/50 px-3 py-2 rounded-lg">
                  https://api-monitoring.devskin.com
                </code>
              </div>
            </div>

            {/* Platform Selection */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center space-x-2 mb-6">
                <SparklesIcon className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {t('sdk.selectPlatform') || 'Select Your Platform'}
                </h2>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {platforms.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setPlatform(p.id)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      platform === p.id
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700'
                    }`}
                  >
                    <div className="text-3xl mb-2">{p.icon}</div>
                    <div className="font-semibold text-gray-900 dark:text-white">{p.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{p.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* SDK Configuration */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center space-x-2 mb-6">
                <SparklesIcon className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {t('sdk.configuration') || 'SDK Configuration'}
                </h2>
              </div>

              <div className="space-y-6">
                {/* Session Tracking */}
                <div className="border-l-4 border-purple-500 pl-4 bg-purple-50/50 dark:bg-purple-900/10 py-3 rounded-r-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Session Tracking
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Track user sessions, screen views, and interactions
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config.sessionTracking.enabled}
                        onChange={(e) => setConfig({ ...config, sessionTracking: { ...config.sessionTracking, enabled: e.target.checked } })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  {config.sessionTracking.enabled && (
                    <div className="space-y-2 mt-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={config.sessionTracking.trackScreenViews}
                          onChange={(e) => setConfig({ ...config, sessionTracking: { ...config.sessionTracking, trackScreenViews: e.target.checked } })}
                          className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Track screen views automatically</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={config.sessionTracking.trackUserInteractions}
                          onChange={(e) => setConfig({ ...config, sessionTracking: { ...config.sessionTracking, trackUserInteractions: e.target.checked } })}
                          className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Track user interactions (taps, gestures)</span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Crash Reporting */}
                <div className="border-l-4 border-red-500 pl-4 bg-red-50/50 dark:bg-red-900/10 py-3 rounded-r-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Crash Reporting
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Capture crashes, errors, and breadcrumbs
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config.crashReporting.enabled}
                        onChange={(e) => setConfig({ ...config, crashReporting: { ...config.crashReporting, enabled: e.target.checked } })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 dark:peer-focus:ring-red-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-red-600"></div>
                    </label>
                  </div>

                  {config.crashReporting.enabled && (
                    <div className="space-y-2 mt-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={config.crashReporting.captureNativeCrashes}
                          onChange={(e) => setConfig({ ...config, crashReporting: { ...config.crashReporting, captureNativeCrashes: e.target.checked } })}
                          className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Capture native crashes (iOS/Android)</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={config.crashReporting.captureBreadcrumbs}
                          onChange={(e) => setConfig({ ...config, crashReporting: { ...config.crashReporting, captureBreadcrumbs: e.target.checked } })}
                          className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Capture breadcrumbs for debugging</span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Performance Monitoring */}
                <div className="border-l-4 border-green-500 pl-4 bg-green-50/50 dark:bg-green-900/10 py-3 rounded-r-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Performance Monitoring
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Track app startup, screen render times, and network
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config.performance.enabled}
                        onChange={(e) => setConfig({ ...config, performance: { ...config.performance, enabled: e.target.checked } })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
                    </label>
                  </div>

                  {config.performance.enabled && (
                    <div className="space-y-2 mt-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={config.performance.trackAppStartTime}
                          onChange={(e) => setConfig({ ...config, performance: { ...config.performance, trackAppStartTime: e.target.checked } })}
                          className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Track app startup time</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={config.performance.trackScreenRenderTime}
                          onChange={(e) => setConfig({ ...config, performance: { ...config.performance, trackScreenRenderTime: e.target.checked } })}
                          className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Track screen render time</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={config.performance.trackNetworkRequests}
                          onChange={(e) => setConfig({ ...config, performance: { ...config.performance, trackNetworkRequests: e.target.checked } })}
                          className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Track network requests</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Installation Steps */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-6">
              {/* Step 1: Installation */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 text-white rounded-full font-bold text-sm shadow-md">
                    1
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('sdk.step1') || 'Step 1'}: {t('sdk.installPackage') || 'Install Package'}
                  </h3>
                </div>
                <CodeBlock
                  code={getInstallCode()}
                  index={0}
                  language={platform === 'ios' ? 'ruby' : platform === 'android' ? 'kotlin' : platform === 'flutter' ? 'yaml' : 'bash'}
                />
              </div>

              {/* Step 2: Initialize */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-pink-500 to-rose-600 text-white rounded-full font-bold text-sm shadow-md">
                    2
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('sdk.step2') || 'Step 2'}: {t('sdk.initializeSDK') || 'Initialize SDK'}
                  </h3>
                </div>
                <CodeBlock
                  code={getInitCode()}
                  index={1}
                  language={platform === 'ios' ? 'swift' : platform === 'android' ? 'kotlin' : platform === 'flutter' ? 'dart' : 'typescript'}
                />
              </div>

              {/* Step 3: Track Events */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-rose-500 to-red-600 text-white rounded-full font-bold text-sm shadow-md">
                    3
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('sdk.step3') || 'Step 3'}: {t('sdk.trackEvents') || 'Track Events & Users'}
                  </h3>
                </div>
                <CodeBlock
                  code={getTrackingExamples()}
                  index={2}
                  language={platform === 'ios' ? 'swift' : platform === 'android' ? 'kotlin' : platform === 'flutter' ? 'dart' : 'typescript'}
                />
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800 rounded-xl shadow-sm p-5 hover:shadow-md transition-all">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-2xl">💥</span>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    {t('sdk.features.crashReporting') || 'Crash Reporting'}
                  </h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('sdk.features.crashReportingDesc') || 'Automatic crash detection with symbolicated stack traces for native and JS errors'}
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl shadow-sm p-5 hover:shadow-md transition-all">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-2xl">⚡</span>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    {t('sdk.features.performance') || 'Performance Monitoring'}
                  </h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('sdk.features.performanceDesc') || 'Track app startup, screen render times, network latency, and slow frames'}
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl shadow-sm p-5 hover:shadow-md transition-all">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-2xl">📊</span>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    {t('sdk.features.analytics') || 'User Analytics'}
                  </h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('sdk.features.analyticsDesc') || 'Track custom events, user journeys, and conversion funnels'}
                </p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border border-orange-200 dark:border-orange-800 rounded-xl shadow-sm p-5 hover:shadow-md transition-all">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-2xl">👤</span>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    {t('sdk.features.sessions') || 'Session Tracking'}
                  </h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('sdk.features.sessionsDesc') || 'Track user sessions with screen views, interactions, and device info'}
                </p>
              </div>

              <div className="bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20 border border-cyan-200 dark:border-cyan-800 rounded-xl shadow-sm p-5 hover:shadow-md transition-all">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-2xl">🌐</span>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    {t('sdk.features.network') || 'Network Monitoring'}
                  </h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('sdk.features.networkDesc') || 'Monitor API calls, response times, errors, and data transfer'}
                </p>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-900/20 dark:to-slate-900/20 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-5 hover:shadow-md transition-all">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-2xl">🔒</span>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    {t('sdk.features.privacy') || 'Privacy First'}
                  </h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('sdk.features.privacyDesc') || 'GDPR compliant with data masking and user consent controls'}
                </p>
              </div>
            </div>

            {/* Quick Links */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                to="/analytics/mobile/journeys"
                className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-6 text-white hover:shadow-lg transition-all group relative overflow-hidden"
              >
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">{t('sdk.quickLinks.journeys') || 'Mobile Journeys'}</h3>
                    <ArrowTopRightOnSquareIcon className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </div>
                  <p className="text-purple-50 text-sm">
                    {t('sdk.quickLinks.journeysDesc') || 'View mobile user sessions, crashes, and screen flows'}
                  </p>
                </div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -z-0" />
              </Link>

              <Link
                to="/analytics/events"
                className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white hover:shadow-lg transition-all group relative overflow-hidden"
              >
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">{t('sdk.quickLinks.events') || 'View Events'}</h3>
                    <ArrowTopRightOnSquareIcon className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </div>
                  <p className="text-green-50 text-sm">
                    {t('sdk.quickLinks.eventsDesc') || 'Analyze custom events and user behavior'}
                  </p>
                </div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -z-0" />
              </Link>

              <Link
                to="/analytics/funnels"
                className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl p-6 text-white hover:shadow-lg transition-all group relative overflow-hidden"
              >
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">{t('sdk.quickLinks.funnels') || 'Create Funnels'}</h3>
                    <ArrowTopRightOnSquareIcon className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </div>
                  <p className="text-orange-50 text-sm">
                    {t('sdk.quickLinks.funnelsDesc') || 'Build conversion funnels for mobile flows'}
                  </p>
                </div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -z-0" />
              </Link>
            </div>

            {/* Documentation Links */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 dark:from-gray-700 dark:to-gray-800 rounded-xl p-6 text-white relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-semibold mb-2 flex items-center space-x-2">
                      <BookOpenIcon className="w-6 h-6" />
                      <span>{t('sdk.needHelp.title') || 'Need Help?'}</span>
                    </h3>
                    <p className="text-gray-300 mb-4">
                      {t('sdk.needHelp.description') || 'Check out our documentation and integration guides for detailed instructions.'}
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <a
                        href="https://github.com/devskin/mobile-sdk"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors backdrop-blur-sm"
                      >
                        <BookOpenIcon className="w-4 h-4" />
                        <span>{t('sdk.needHelp.fullDocs') || 'Full Documentation'}</span>
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-500/20 to-pink-600/20 rounded-full blur-3xl -z-0" />
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}
